import { api } from '../api/client';

import { API_ITEMS_URL } from '../utils';
import { normalizeId } from '../utils/ids';

/**
 * A viewer's saved state on one related list (KPR-722): the filter they added
 * and their grid/table choice, kept on their account so it follows them to
 * another device. The server keys it on the card that defines the list and
 * the list's id; the client names it through the item being viewed, so the
 * same gates as the list read apply.
 *
 * It is read with the switcher's counts (`fetchRelatedLists`, `viewerState`
 * on each list). The search term is never part of it: the server takes a
 * top-level `text ~` out of the filter, and the caller leaves it out too.
 */

const VIEWS = ['grid', 'table'];

const stateUrl = (itemId, listId) =>
	`${API_ITEMS_URL}/${normalizeId(itemId)}/related-lists/${encodeURIComponent(listId)}/viewer-state`;

/**
 * The server's `viewerState` as the client holds it: `{ filter, view }`, each
 * a value or null, or null when nothing is saved (or the answer is not one).
 */
export function readViewerState(raw) {
	if (!raw || typeof raw !== 'object') return null;
	const filter = typeof raw.filter === 'string' && raw.filter.trim() ? raw.filter : null;
	const view = VIEWS.includes(raw.view) ? raw.view : null;
	return filter || view ? { filter, view } : null;
}

/**
 * Saves the caller's whole state for the list (it replaces what was saved):
 * `PUT /api/items/{id}/related-lists/{listId}/viewer-state`. Both null forgets
 * it. Resolves to the state as stored (the filter canonical), or null.
 */
export async function saveRelatedListState(itemId, listId, { filter = null, view = null } = {}) {
	const { data } = await api.put(stateUrl(itemId, listId), {
		filter: filter || null,
		view: VIEWS.includes(view) ? view : null,
	});
	return readViewerState(data?.viewerState);
}

/** Reset: forgets the caller's saved state for the list. */
export async function resetRelatedListState(itemId, listId) {
	await api.delete(stateUrl(itemId, listId));
}
